import { type FormEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { api, ApiError } from "./api";
import { bestDiscount, filterCount, formatDate, formatNative, formatRub, gameLocalization, gamePlatforms, rankedOffers, visibleGames, type PlatformFilter } from "./catalog";
import { CardArtwork } from "./CardArtwork";
import { OfferPills } from "./OfferPills";
import { NotificationCenter } from "./NotificationCenter";
import { clearCatalogState, readCatalogState, saveCatalogState } from "./catalogState";
import { AddGameDialog, GameEditor, ProbeStatusDialog, SettingsDialog } from "./Editor";
import { mockGames, mockHistory, mockSession } from "./mock";
import { WishlistRefresh } from "./WishlistRefresh";
import { PriceChart } from "./PriceChart";
import { openExternal, setTelegramBackHandler, telegramInitData } from "./telegram";
import type { AuthConfig, FilterName, HistoryPoint, ProbeSummary, Session, SortName, WishlistGame, WishlistOffer } from "./types";

const FILTERS: Array<{ name: FilterName; label: string }> = [
  { name: "all", label: "Все" },
  { name: "sale", label: "Скидки" },
  { name: "russian", label: "Русский" },
  { name: "missing", label: "Без цены" },
  { name: "upcoming", label: "Будущие" },
  { name: "ps_plus", label: "PS+" },
];

const STATUS_LABELS = {
  released: "Вышла",
  preorder: "Предзаказ",
  waiting: "Ожидается",
};

const LOCALIZATION_LABELS = {
  unknown: "Русский неизвестен",
  none: "Без русского",
  text: "Русский текст",
  text_audio: "Русский текст и звук",
};

const GAME_LOCALIZATION_LABELS = {
  text_audio: "RU · текст + звук",
  text: "RU · текст",
  none: "Без русского",
  unknown: "Язык не проверен",
};

function gameIdFromHash(): number | null {
  const match = window.location.hash.match(/^#\/game\/(\d+)$/);
  return match ? Number(match[1]) : null;
}

function notificationIdFromHash(): number | null | undefined {
  const match = window.location.hash.match(/^#\/notifications(?:\/(\d+))?$/);
  return match ? (match[1] ? Number(match[1]) : null) : undefined;
}

function browserLogin(provider: "browser" | "authentik") {
  window.location.href = `/api/v1/auth/${provider}/start?return_to=${encodeURIComponent("/" + window.location.hash)}`;
}

function isDemo(): boolean {
  return import.meta.env.DEV && new URLSearchParams(window.location.search).has("demo");
}

function isLocalDatabase(): boolean {
  return import.meta.env.DEV && new URLSearchParams(window.location.search).has("local");
}

function GameCard({ game, onOpen }: { game: WishlistGame; onOpen: () => void }) {
  const [failedPoster, setFailedPoster] = useState<string | null>(null);
  const release = formatDate(game.release_date);
  const releaseLabel = game.release_kind === "early_access" ? "Ранний доступ" : "Релиз";
  const localization = gameLocalization(game);
  return (
    <button data-game-id={game.id} className={game.poster_url && failedPoster !== game.poster_url ? "game-card game-card-with-poster" : "game-card"} type="button" onClick={onOpen}>
      {game.poster_url ? <CardArtwork key={game.poster_url} url={game.poster_url} onUnavailable={() => setFailedPoster(game.poster_url)} /> : null}
      <span className="game-card-body">
        <span className="game-card-topline">
          <span className="game-card-badges">
            {game.status !== "released" ? <span className={`status status-${game.status}`}>{STATUS_LABELS[game.status]}</span> : null}
            {gamePlatforms(game).map(platform => <span className="platform-badge" key={platform}>{platform === "pc" ? "ПК" : "PS"}</span>)}
            <span className={`localization-badge localization-${localization}`}>
              {GAME_LOCALIZATION_LABELS[localization]}
            </span>
            {game.alarm_triggered ? <span className="alarm-badge">🔔 Порог достигнут</span> : null}
          </span>
        </span>
        <strong className="game-title" title={game.title}>{game.title}</strong>
        <span className={game.best_price_rub === null ? "best-price muted" : "best-price"}>
          {formatRub(game.best_price_rub)}
          {game.best_price_kind === "ps_plus" ? <small>с PS+</small> : null}
          {bestDiscount(game) ? <small className="discount">−{bestDiscount(game)}%</small> : null}
        </span>
        {release ? <span className="release-date">{releaseLabel} {release}</span> : null}
        <OfferPills game={game} />
      </span>
    </button>
  );
}

function OfferRow({ offer }: { offer: WishlistOffer }) {
  const localization = LOCALIZATION_LABELS[offer.localization_status];
  const availability = [
    offer.early_access_date ? `Ранний доступ ${formatDate(offer.early_access_date)}` : null,
    offer.release_date ? `Релиз ${formatDate(offer.release_date)}` : null,
  ].filter(Boolean).join(" · ");
  const content = (
    <>
      <span className="offer-heading">
        <strong>{offer.label}</strong>
        {offer.provider_enabled === false ? <span className="offer-meta">Отключён в настройках · сохранённая цена</span> : null}
        {offer.is_best ? <span className="best-badge">Лучшая цена</span> : null}
      </span>
      <span className="offer-price-line">
        <strong>{formatRub(offer.price_rub)}</strong>
        {offer.currency !== "RUB" && offer.price_minor !== null ? <span>{formatNative(offer.price_minor, offer.currency)}</span> : null}
        {offer.discount_percent ? <span className="discount">−{offer.discount_percent}%</span> : null}
      </span>
      {offer.ps_plus_price_rub !== null ? (
        <span className="ps-plus-line">
          PS+ · <strong>{formatRub(offer.ps_plus_price_rub)}</strong>
          {offer.ps_plus_discount_percent ? ` · −${offer.ps_plus_discount_percent}%` : ""}
        </span>
      ) : null}
      <span className="offer-meta">
        {localization}
        {offer.ps_plus_tier ? ` · В каталоге ${offer.ps_plus_tier}` : ""}
        {offer.steam_personalized ? " · 👤 Цена с учётом библиотеки" : ""}
      </span>
      {offer.price_unavailable_reason ? <span className="offer-meta">{offer.price_unavailable_reason}</span> : null}
      {availability ? <span className="offer-meta offer-availability">{availability}</span> : null}
      {offer.marketplace ? <span className="offer-meta">
        Минимум среди проверенных · аккаунт KZ · {offer.marketplace.target.edition}
        {offer.marketplace.winner ? <><br />
          {offer.marketplace.winner.delivery_type === "gift" ? "Гифт" : "Ключ"}
          {" · "}{offer.marketplace.winner.seller_name}<br />
          На сайте выберите: {offer.marketplace.winner.selection.map(choice => choice.label).join(" · ") || offer.marketplace.winner.territory}<br />
          {offer.marketplace.winner.price_note}
        </> : null}
        {offer.marketplace.coverage.limited ? <><br />Проверена ограниченная часть выдачи</> : null}
        {offer.marketplace.coverage.partial ? <><br />Часть предложений не удалось проверить</> : null}
      </span> : null}
    </>
  );
  if (!offer.url) return <div className="offer-row">{content}</div>;
  return <button type="button" className="offer-row" onClick={() => openExternal(offer.url)}>{content}<span className="external-arrow">↗</span></button>;
}

function lastChecked(game: WishlistGame): string {
  const checks = game.offers.flatMap(offer => {
    if (!offer.checked_at) return [];
    const value = offer.checked_at.replace(" ", "T");
    const time = Date.parse(/[Zz]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`);
    return Number.isFinite(time) ? [time] : [];
  });
  return checks.length ? new Intl.DateTimeFormat("ru-RU", { timeZone: "Europe/Moscow", dateStyle: "short", timeStyle: "short" }).format(Math.max(...checks)) + " МСК" : "ещё не выполнялась";
}

function GameDetail({ gameId, demo, onBack, onCatalogChanged, onDeleted }: { gameId: number; demo: boolean; onBack: () => void; onCatalogChanged: () => Promise<void>; onDeleted: () => void }) {
  const [game, setGame] = useState<WishlistGame | null>(demo ? mockGames.find((item) => item.id === gameId) ?? null : null);
  const [history, setHistory] = useState<HistoryPoint[]>(demo ? mockHistory[gameId] ?? [] : []);
  const [error, setError] = useState(false);
  const [editing, setEditing] = useState(false);
  const [failedPoster, setFailedPoster] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);
  const refreshRunning = useRef(false);
  useEffect(() => setTelegramBackHandler(onBack), [onBack]);
  const reload = useCallback(async () => {
    if (demo) return;
    const [nextGame, nextHistory] = await Promise.all([api.game(gameId), api.history(gameId)]);
    setGame(nextGame);
    setHistory(nextHistory);
    await onCatalogChanged();
  }, [demo, gameId, onCatalogChanged]);
  useEffect(() => {
    if (demo) return;
    void reload().catch(() => setError(true));
  }, [demo, reload]);
  useEffect(() => {
    if (demo) return;
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void reload().catch(() => setRefreshMessage("Не удалось обновить данные карточки."));
    };
    document.addEventListener("visibilitychange", refreshWhenVisible);
    window.addEventListener("focus", refreshWhenVisible);
    return () => {
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      window.removeEventListener("focus", refreshWhenVisible);
    };
  }, [demo, reload]);
  const refreshPrices = async () => {
    if (refreshRunning.current || demo) return;
    refreshRunning.current = true; setRefreshing(true); setRefreshMessage(null);
    try {
      const result = await api.refreshGame(gameId);
      await reload();
      setRefreshMessage(result.failed_stores.length ? `Часть источников не ответила: ${result.failed_stores.join(", ")}. Сохранены последние известные цены.` : "Цены обновлены.");
    } catch { setRefreshMessage("Не удалось обновить цены. Попробуйте ещё раз."); }
    finally { refreshRunning.current = false; setRefreshing(false); }
  };
  if (error) return <StatePanel title="Игра недоступна" text="Она могла быть удалена. Вернитесь к списку или повторите попытку позже." action={onBack} actionLabel="К списку" />;
  if (!game) return <Loading />;
  return (
    <main className="detail-page">
      <button className="browser-back" type="button" onClick={onBack}>← Wishlist</button>
      <section className={game.poster_url && failedPoster !== game.poster_url ? "detail-hero detail-hero-with-poster" : "detail-hero"}>
        {game.poster_url ? <CardArtwork key={game.poster_url} url={game.poster_url} eager onUnavailable={() => setFailedPoster(game.poster_url)} /> : null}
        <div className="detail-hero-copy">
          <div>
            {game.status !== "released" ? <span className={`status status-${game.status}`}>{STATUS_LABELS[game.status]}</span> : null}
            <h1>{game.title}</h1>
          </div>
          <div className="hero-side">
            <div className="hero-price"><span>Лучшая цена</span><strong>{formatRub(game.best_price_rub)}</strong>{game.best_price_kind === "ps_plus" ? <small>по подписке PS+</small> : null}</div>
            {game.alarm_triggered ? <div className="alarm-notice">🔔 Цена достигла порога {formatRub(game.alarm_threshold_rub)}</div> : null}
            {!demo ? <div className="detail-actions"><button type="button" className="bot-edit-button" disabled={refreshing} onClick={() => void refreshPrices()}>{refreshing ? "Обновляем…" : "Обновить цены"}</button><button type="button" className="bot-edit-button" onClick={() => setEditing(true)}>Изменить</button></div> : null}
          </div>
        </div>
      </section>
      {refreshMessage ? <p className="catalog-action-message" role="status">{refreshMessage}</p> : null}
      <p className="last-price-check">Последняя проверка: {lastChecked(game)}</p>
      <section className="detail-section">
        <div className="section-heading"><div><span className="eyebrow">Сейчас</span><h2>Где купить</h2></div><span>{game.offers.length} источников</span></div>
        <div className="offer-list">{rankedOffers(game).map((offer) => <OfferRow key={offer.id} offer={offer} />)}</div>
      </section>
      <section className="detail-section chart-section">
        <div className="section-heading"><div><span className="eyebrow">Динамика</span><h2>История цены</h2></div><span>в рублях по текущему курсу</span></div>
        <PriceChart points={history} />
      </section>
      {editing ? <GameEditor game={game} onClose={() => setEditing(false)} onChanged={reload} onDeleted={onDeleted} /> : null}
    </main>
  );
}

function Loading() {
  return <div className="loading" aria-label="Загрузка"><span /><span /><span /></div>;
}

function StatePanel({ title, text, action, actionLabel }: { title: string; text: string; action?: () => void; actionLabel?: string }) {
  return <main className="state-panel"><div className="state-mark">PT</div><h1>{title}</h1><p>{text}</p>{action ? <button type="button" onClick={action}>{actionLabel}</button> : null}</main>;
}

function LoginPanel({ config, pending, error, onLocalLogin }: { config: AuthConfig; pending: boolean; error: string | null; onLocalLogin: (username: string, password: string) => Promise<void> }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void onLocalLogin(username, password);
  };
  const miniAppOnly = config.mode === "miniapp";
  return (
    <main className="state-panel login-panel">
      <div className="state-mark">PT</div>
      <h1>Ваш Wishlist</h1>
      <p>{miniAppOnly ? "Откройте Wishlist через кнопку меню Telegram — вход произойдёт автоматически." : "Войдите в защищённую веб-версию Wishlist."}</p>
      {config.local_auth ? (
        <form className="login-form" onSubmit={submit}>
          <label>Пользователь<input autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} required /></label>
          <label>Пароль<input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
          {error ? <span className="login-error" role="alert">{error}</span> : null}
          <button type="submit" disabled={pending}>{pending ? "Проверяем…" : "Войти"}</button>
        </form>
      ) : null}
      {config.telegram_oidc ? <button className="secondary-login" type="button" onClick={() => browserLogin("browser")}>Войти через Telegram</button> : null}
      {config.authentik_oidc ? <button className="secondary-login" type="button" onClick={() => browserLogin("authentik")}>Войти через Authentik</button> : null}
    </main>
  );
}

export default function App() {
  const demo = isDemo();
  const localDatabase = isLocalDatabase();
  const [session, setSession] = useState<Session | null>(demo ? mockSession : null);
  const [games, setGames] = useState<WishlistGame[]>(demo ? mockGames : []);
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const [authState, setAuthState] = useState<"loading" | "ready" | "login" | "forbidden" | "error">(demo ? "ready" : "loading");
  const [localAuthPending, setLocalAuthPending] = useState(false);
  const [localAuthError, setLocalAuthError] = useState<string | null>(null);
  const [initialCatalog] = useState(readCatalogState);
  const catalogPosition = useRef(initialCatalog);
  const restoreCatalog = useRef(true);
  const [query, setQuery] = useState(initialCatalog.query);
  const [filter, setFilter] = useState<FilterName>(initialCatalog.filter);
  const [sort, setSort] = useState<SortName>(initialCatalog.sort);
  const [platform, setPlatform] = useState<PlatformFilter>(initialCatalog.platform);
  const [gameId, setGameId] = useState<number | null>(gameIdFromHash());
  const [reportEditId, setReportEditId] = useState<number | null>(null);
  const [notificationId, setNotificationId] = useState<number | null | undefined>(notificationIdFromHash);
  const [adding, setAdding] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [probesOpen, setProbesOpen] = useState(false);
  const [probes, setProbes] = useState<ProbeSummary | null>(null);

  const load = useCallback(async () => {
    try {
      let activeSession: Session;
      if (localDatabase) {
        activeSession = await api.loginLocalDev();
      } else {
        const config = await api.authConfig();
        setAuthConfig(config);
        try {
          activeSession = await api.session();
        } catch (error) {
          if (!(error instanceof ApiError) || error.status !== 401) throw error;
          const initData = telegramInitData();
          if (!config.telegram_mini_app || !initData) {
            setAuthState("login");
            return;
          }
          activeSession = await api.loginMiniApp(initData);
        }
      }
      const wishlist = await api.wishlist();
      setSession(activeSession);
      setGames(wishlist);
      setAuthState("ready");
      void api.probes().then(setProbes).catch(() => setProbes(null));
    } catch (error) {
      setAuthState(error instanceof ApiError && error.status === 401 ? "login" : error instanceof ApiError && error.status === 403 ? "forbidden" : "error");
    }
  }, [localDatabase]);

  useEffect(() => {
    if (!demo) void load();
  }, [demo, load]);
  useEffect(() => {
    const expired = () => { setSession(null); setAuthState("login"); };
    window.addEventListener("pt-session-expired", expired);
    return () => window.removeEventListener("pt-session-expired", expired);
  }, []);
  useEffect(() => {
    if (demo || authState !== "ready") return;
    let lastActivity = Date.now();
    let pending = false;
    const active = (event: Event) => {
      if (!event.isTrusted || document.visibilityState !== "visible" || pending || Date.now() - lastActivity < 60_000) return;
      lastActivity = Date.now(); pending = true;
      void api.activity().catch(error => {
        if (error instanceof ApiError && error.status === 401) { setSession(null); setAuthState("login"); }
      }).finally(() => { pending = false; });
    };
    const events = ["pointerdown", "keydown", "wheel", "touchstart"];
    events.forEach(event => window.addEventListener(event, active, { passive: true }));
    return () => events.forEach(event => window.removeEventListener(event, active));
  }, [demo, authState]);
  useEffect(() => {
    const previous = history.scrollRestoration;
    history.scrollRestoration = "manual";
    const onHash = () => { restoreCatalog.current = true; setGameId(gameIdFromHash()); setNotificationId(notificationIdFromHash()); };
    window.addEventListener("hashchange", onHash);
    window.addEventListener("popstate", onHash);
    return () => { window.removeEventListener("hashchange", onHash); window.removeEventListener("popstate", onHash); history.scrollRestoration = previous; };
  }, []);

  useEffect(() => {
    catalogPosition.current = { ...catalogPosition.current, query, filter, sort, platform };
    if (authState === "ready") saveCatalogState(catalogPosition.current);
  }, [query, filter, sort, platform, authState]);

  useLayoutEffect(() => {
    if (authState !== "ready") return;
    if (gameId !== null || notificationId !== undefined) { window.scrollTo(0, 0); return; }
    if (!restoreCatalog.current) return;
    restoreCatalog.current = false;
    const position = catalogPosition.current;
    const restore = () => {
      const anchor = position.anchorId ? document.querySelector(`[data-game-id="${position.anchorId}"]`) : null;
      const top = anchor ? anchor.getBoundingClientRect().top + window.scrollY - position.anchorTop : position.scrollY;
      window.scrollTo(0, Math.max(0, top));
      if (anchor instanceof HTMLElement) anchor.focus({ preventScroll: true });
    };
    restore();
    // Pill measurement changes row heights during the first layout; restore again after it settles.
    let frame = requestAnimationFrame(() => { frame = requestAnimationFrame(restore); });
    return () => cancelAnimationFrame(frame);
  }, [authState, gameId, notificationId]);

  const rememberCatalog = useCallback((anchorId: number | null = null) => {
    const anchor = anchorId ? document.querySelector(`[data-game-id="${anchorId}"]`) : [...document.querySelectorAll("[data-game-id]")].find(element => element.getBoundingClientRect().bottom > 0);
    catalogPosition.current = { ...catalogPosition.current, scrollY: window.scrollY,
      anchorId: anchor ? Number(anchor.getAttribute("data-game-id")) : null, anchorTop: anchor?.getBoundingClientRect().top ?? 0 };
    saveCatalogState(catalogPosition.current);
  }, []);
  useEffect(() => {
    const remember = () => { if (gameId === null && notificationId === undefined && authState === "ready") rememberCatalog(); };
    window.addEventListener("pagehide", remember);
    return () => window.removeEventListener("pagehide", remember);
  }, [gameId, notificationId, authState, rememberCatalog]);

  const closeDetail = useCallback(() => {
    restoreCatalog.current = true;
    if (history.state?.ptCatalog || history.state?.ptBackTo) { history.back(); return; }
    history.replaceState(null, "", window.location.pathname + window.location.search);
    setGameId(null);
    setNotificationId(undefined);
  }, []);
  const openGame = (id: number) => {
    if (notificationId === undefined) rememberCatalog(id);
    history.pushState(notificationId === undefined ? { ptCatalog: true } : { ptBackTo: window.location.hash }, "", `#/game/${id}`);
    setNotificationId(undefined);
    setGameId(id);
  };
  const openNotifications = (id: number | null = null) => {
    if (notificationId === undefined && gameId === null) rememberCatalog();
    history.pushState(notificationId === undefined ? { ptCatalog: true } : { ptBackTo: window.location.hash }, "", `#/notifications${id !== null ? `/${id}` : ""}`);
    setGameId(null); setNotificationId(id);
  };
  const loginLocal = async (username: string, password: string) => {
    setLocalAuthPending(true);
    setLocalAuthError(null);
    try {
      const activeSession = await api.loginLocal(username, password);
      const wishlist = await api.wishlist();
      setSession(activeSession);
      setGames(wishlist);
      setAuthState("ready");
      void api.probes().then(setProbes).catch(() => setProbes(null));
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setLocalAuthError("Неверный пользователь или пароль.");
      } else if (error instanceof ApiError && error.status === 429) {
        setLocalAuthError("Слишком много попыток. Повторите вход позже.");
      } else {
        setAuthState("error");
      }
    } finally {
      setLocalAuthPending(false);
    }
  };
  const logout = async () => {
    await api.logout();
    setSession(null);
    setGames([]);
    setSettingsOpen(false);
    setProbesOpen(false);
    setGameId(null);
    setNotificationId(undefined);
    setLocalAuthError(null);
    window.location.hash = "";
    clearCatalogState();
    setAuthState("login");
  };
  const visible = useMemo(() => visibleGames(games, query, filter, sort, platform), [games, query, filter, sort, platform]);
  const reportEditGame = games.find(game => game.id === reportEditId);
  const reloadWishlist = useCallback(async () => {
    if (!demo) setGames(await api.wishlist());
  }, [demo]);
  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible" && authState === "ready") {
        void reloadWishlist().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", refreshWhenVisible);
    window.addEventListener("focus", refreshWhenVisible);
    return () => {
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      window.removeEventListener("focus", refreshWhenVisible);
    };
  }, [authState, reloadWishlist]);

  if (authState === "loading") return <Loading />;
  if (authState === "login") return authConfig ? <LoginPanel config={authConfig} pending={localAuthPending} error={localAuthError} onLocalLogin={loginLocal} /> : <Loading />;
  if (authState === "forbidden") return <StatePanel title="Доступ закрыт" text="Этот Wishlist доступен только владельцу бота." />;
  if (authState === "error") return <StatePanel title="Не удалось загрузить Wishlist" text="Проверьте соединение и попробуйте ещё раз." action={() => void load()} actionLabel="Повторить" />;
  if (gameId !== null) return <GameDetail key={gameId} gameId={gameId} demo={demo} onBack={closeDetail} onCatalogChanged={reloadWishlist} onDeleted={() => { void reloadWishlist(); closeDetail(); }} />;
  if (notificationId !== undefined) return <NotificationCenter eventId={notificationId} demo={demo} onBack={closeDetail} onOpenGame={openGame} onOpenEvent={openNotifications} />;

  return (
    <main className="catalog-page">
      <header className="app-header">
        <div><span className="eyebrow">PT Wishlist</span><h1>Игры, которые<br />стоит дождаться.</h1></div>
        <div className="header-side"><div className="header-summary"><span>{games.length}<small>в Wishlist</small></span><span>{filterCount(games, "sale")}<small>со скидкой</small></span></div>{!demo ? <div className="header-actions"><button type="button" onClick={() => setAdding(true)}>+ Игра</button><WishlistRefresh onUpdated={reloadWishlist} games={games} onOpenGame={openGame} onEditGame={setReportEditId} /><button className={`probe-indicator ${probes?.overall ?? "yellow"}`} type="button" onClick={() => setProbesOpen(true)} aria-label="Состояние источников" title="Состояние источников"><span className="probe-dot" /></button><button type="button" onClick={() => setSettingsOpen(true)}>Настройки</button></div> : null}</div>
      </header>
      {!demo ? <button className="notification-center-link" type="button" onClick={() => openNotifications()}>Уведомления</button> : null}
      <section className="toolbar" aria-label="Поиск и сортировка">
        <label className="search-box"><span>⌕</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Найти игру" aria-label="Найти игру" />{query ? <button type="button" onClick={() => setQuery("")} aria-label="Очистить поиск">×</button> : null}</label>
        <select value={sort} onChange={(event) => setSort(event.target.value as SortName)} aria-label="Сортировка"><option value="default">По статусу</option><option value="price">Сначала дешевле</option><option value="discount">По скидке</option><option value="title">По названию</option></select>
      </section>
      <nav className="filters" aria-label="Фильтры Wishlist">
        {FILTERS.map((item) => <button key={item.name} type="button" className={filter === item.name ? "active" : ""} onClick={() => setFilter(item.name)}>{item.label}<span>{filterCount(games, item.name, platform)}</span></button>)}
      </nav>
      <nav className="platform-filters" aria-label="Платформа игры">{([['all', 'Все платформы'], ['pc', 'ПК'], ['ps', 'PS']] as const).map(([value, label]) => <button type="button" key={value} aria-pressed={platform === value} onClick={() => setPlatform(value)}>{label}</button>)}</nav>
      <div className="catalog-meta"><span>{visible.length} {visible.length === 1 ? "игра" : "игр"}</span>{session ? <span>для {session.name}</span> : null}</div>
      {visible.length ? <section className="game-grid">{visible.map((game) => <GameCard key={game.id} game={game} onOpen={() => openGame(game.id)} />)}</section> : <section className="empty-list"><strong>Ничего не найдено</strong><span>Измените поиск или выберите другой фильтр.</span><button type="button" onClick={() => { setQuery(""); setFilter("all"); setPlatform("all"); }}>Показать всё</button></section>}
      <footer>Цены пересчитываются с учётом ваших курсов и комиссий.</footer>
      {reportEditGame ? <GameEditor game={reportEditGame} onClose={() => setReportEditId(null)} onChanged={reloadWishlist} onDeleted={() => { setReportEditId(null); void reloadWishlist(); }} /> : null}
      {adding ? <AddGameDialog onClose={() => setAdding(false)} onCreated={(id) => { setAdding(false); void reloadWishlist(); openGame(id); }} /> : null}
      {settingsOpen ? <SettingsDialog onClose={() => setSettingsOpen(false)} onSaved={reloadWishlist} onLogout={authConfig && (authConfig.local_auth || authConfig.telegram_oidc || authConfig.authentik_oidc) ? logout : undefined} /> : null}
      {probesOpen ? <ProbeStatusDialog onClose={() => setProbesOpen(false)} onSaved={setProbes} /> : null}
    </main>
  );
}
